import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import {
  ChevronDown,
  Expand,
  Maximize2,
  Minus,
  Pause,
  Play,
  Plus,
  RotateCcw,
  RotateCw,
  Shrink,
  Target,
} from "lucide-react";
import { SimErrorBoundary } from "@/components/simulations/sim-error-boundary";
import { LabStage } from "./lab-stage";
import type { LabModule, LabValues } from "@/lib/labs/types";
import { cn } from "@/lib/utils";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduced(mq.matches);
    set();
    mq.addEventListener("change", set);
    return () => mq.removeEventListener("change", set);
  }, []);
  return reduced;
}

function Collapsible({
  title,
  children,
  tone = "primary",
}: {
  title: string;
  children: React.ReactNode;
  tone?: "primary" | "sky" | "emerald";
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass overflow-hidden rounded-[20px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3.5 text-left transition-transform active:scale-[0.99]"
      >
        <span className="text-[12.5px] font-semibold">{title}</span>
        <ChevronDown
          className={cn(
            "size-4 text-muted-foreground transition-transform duration-300",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className={cn(
            "px-4 pb-4",
            tone === "sky" && "[--dot:theme(colors.sky.400)]",
            tone === "emerald" && "[--dot:theme(colors.emerald.400)]",
          )}
        >
          {children}
        </motion.div>
      ) : null}
    </div>
  );
}

function Bullets({ items, dot }: { items: string[]; dot: string }) {
  return (
    <ul className="space-y-2">
      {items.map((line) => (
        <li key={line} className="flex gap-2 text-[12.5px] leading-relaxed">
          <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", dot)} />
          {line}
        </li>
      ))}
    </ul>
  );
}

function ObservationGraph({
  lab,
  state,
  values,
}: {
  lab: LabModule<unknown>;
  state: unknown;
  values: LabValues;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, []);
  let pts: { x: number; y: number }[] = [];
  try {
    pts = (lab.observation?.series(state, values) ?? []).filter(
      (p) => p && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)),
    );
  } catch {
    pts = [];
  }
  if (pts.length < 2) {
    return (
      <p className="py-8 text-center text-[11.5px] text-muted-foreground">
        Run the experiment first — readings will plot here.
      </p>
    );
  }
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const W = 320;
  const H = 160;
  const pad = 20;
  const px = (x: number) => pad + ((x - minX) / (maxX - minX || 1)) * (W - pad * 2);
  const py = (y: number) => H - pad - ((y - minY) / (maxY - minY || 1)) * (H - pad * 2);
  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${px(p.x).toFixed(1)} ${py(p.y).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-[160px] w-full" role="img" aria-label="Observation graph">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
      <line x1={pad} y1={pad} x2={pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
      <path d={d} fill="none" className="stroke-primary" strokeWidth={2} strokeLinecap="round" />
      <text x={W - pad} y={H - 5} textAnchor="end" className="fill-muted-foreground text-[9px]">
        {lab.observation?.xLabel}
      </text>
      <text x={4} y={12} className="fill-muted-foreground text-[9px]">
        {lab.observation?.yLabel}
      </text>
    </svg>
  );
}

export function LabPlayer(props: { lab: LabModule<unknown> }) {
  return (
    <SimErrorBoundary resetKey={props.lab?.concept}>
      <LabPlayerInner {...props} />
    </SimErrorBoundary>
  );
}

function LabPlayerInner({ lab }: { lab: LabModule<unknown> }) {
  const controls = lab.controls ?? [];
  const initial = Object.fromEntries(controls.map((c) => [c.key, Number(c.value) || 0])) as LabValues;
  const [values, setValues] = useState<LabValues>(initial);
  const [playing, setPlaying] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [rotated, setRotated] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [tab, setTab] = useState<"Experiment" | "Observation">("Experiment");
  const [fullscreen, setFullscreen] = useState(false);
  const [readouts, setReadouts] = useState<{ label: string; value: string }[]>([]);
  const [solved, setSolved] = useState(false);
  const [openAnswer, setOpenAnswer] = useState<number | null>(null);
  const stateRef = useRef<unknown>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const id = setInterval(() => {
      const s = stateRef.current;
      if (!s) return;
      try {
        setReadouts(lab.readouts(s, values) ?? []);
      } catch {
        /* ignore a bad frame of readings */
      }
      try {
        if (lab.challenge?.check(s, values)) setSolved(true);
      } catch {
        /* ignore */
      }
    }, 200);
    return () => clearInterval(id);
  }, [lab, values]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const replay = () => {
    setResetKey((k) => k + 1);
    setPlaying(true);
    setSolved(false);
  };
  const reset = () => {
    setValues(initial);
    setZoom(1);
    setRotated(false);
    replay();
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stageRef.current?.requestFullscreen?.().catch(() => setFullscreen(false));
  };

  return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed text-muted-foreground">{lab.concept}</p>

      <div className="flex gap-1 rounded-full bg-surface/60 p-1">
        {(["Experiment", "Observation"] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setTab(item)}
            className={cn(
              "relative flex-1 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors",
              tab === item ? "text-primary-foreground" : "text-muted-foreground",
            )}
          >
            {tab === item ? (
              <motion.span
                layoutId="lab-tab"
                className="absolute inset-0 -z-10 rounded-full [background-image:var(--gradient-brand)]"
                transition={{ type: "spring", stiffness: 300, damping: 28 }}
              />
            ) : null}
            {item}
          </button>
        ))}
      </div>

      {tab === "Experiment" ? (
        <div className="space-y-4 xl:grid xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start xl:gap-4 xl:space-y-0">
          <div className="space-y-4">
          <div ref={stageRef} className="glass overflow-hidden rounded-[22px] bg-background p-2">
            <div
              className="transition-transform duration-500"
              style={{ transform: rotated ? "rotate(-90deg) scale(0.86)" : "none" }}
            >
              <LabStage
                lab={lab}
                values={values}
                playing={playing}
                zoom={zoom}
                resetKey={resetKey}
                height={300}
                reducedMotion={reduced}
                onState={(s) => {
                  stateRef.current = s;
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 px-2 pb-1 pt-2">
              <button
                type="button"
                onClick={() => setPlaying((v) => !v)}
                className="flex flex-1 items-center justify-center gap-2 rounded-full [background-image:var(--gradient-brand)] px-4 py-2 text-[12px] font-semibold text-primary-foreground transition-transform active:scale-95"
              >
                {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
                {playing ? "Pause" : "Play"}
              </button>
              <button
                type="button"
                onClick={replay}
                className="glass flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold transition-transform active:scale-95"
              >
                <RotateCcw className="size-4" /> Replay
              </button>
              <button
                type="button"
                onClick={reset}
                className="glass flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold transition-transform active:scale-95"
              >
                Reset
              </button>
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => setZoom((z) => Math.max(0.6, Number((z - 0.15).toFixed(2))))}
                className="glass grid size-9 place-items-center rounded-full transition-transform active:scale-90"
              >
                <Minus className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => setZoom((z) => Math.min(2.2, Number((z + 0.15).toFixed(2))))}
                className="glass grid size-9 place-items-center rounded-full transition-transform active:scale-90"
              >
                <Plus className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Rotate view"
                onClick={() => setRotated((r) => !r)}
                className="glass grid size-9 place-items-center rounded-full transition-transform active:scale-90"
              >
                <RotateCw className="size-4" />
              </button>
              <button
                type="button"
                aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={toggleFullscreen}
                className="glass grid size-9 place-items-center rounded-full transition-transform active:scale-90"
              >
                {fullscreen ? <Shrink className="size-4" /> : <Expand className="size-4" />}
              </button>
            </div>
          </div>

          {(lab.actions ?? []).length ? (
            <div className="flex flex-wrap gap-2">
              {(lab.actions ?? []).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    const s = stateRef.current;
                    if (!s) return;
                    try {
                      lab.onAction?.(s, a.id, values, { width: 320, height: 300 });
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="glass rounded-full px-4 py-2 text-[12px] font-semibold transition-transform active:scale-95"
                >
                  {a.label}
                </button>
              ))}
            </div>
          ) : null}
          </div>

          <div className="space-y-4">
          {readouts.length ? (
            <div className="glass grid grid-cols-2 gap-2 rounded-[20px] p-3">
              {readouts.map((r) => (
                <div key={r.label} className="rounded-xl bg-surface/60 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.label}</p>
                  <p className="text-[13px] font-semibold tabular-nums">{r.value}</p>
                </div>
              ))}
            </div>
          ) : null}

          {controls.length ? (
            <div className="glass space-y-3 rounded-[20px] p-4">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Lab settings
              </h2>
              {controls.map((c) => (
                <label key={c.key} className="block">
                  <span className="flex items-center justify-between text-[12px]">
                    <span className="font-medium">{c.label}</span>
                    <span className="font-semibold tabular-nums">
                      {Number(values[c.key] ?? c.value).toFixed(2)}
                      {c.unit ? ` ${c.unit}` : ""}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={c.min}
                    max={c.max}
                    step={c.step}
                    value={Number.isFinite(Number(values[c.key])) ? Number(values[c.key]) : c.value}
                    onChange={(e) => {
                      const raw = Number(e.target.value);
                      const n = Number.isFinite(raw)
                        ? Math.min(Math.max(raw, c.min), c.max)
                        : c.value;
                      setValues((v) => (v[c.key] === n ? v : { ...v, [c.key]: n }));
                    }}
                    className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-surface accent-primary"
                  />
                </label>
              ))}
            </div>
          ) : null}

          <div
            className={cn(
              "glass rounded-[20px] p-4 transition-colors",
              solved ? "border border-emerald-400/35 bg-emerald-500/[0.07]" : "",
            )}
          >
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
              <Target className="size-3.5" /> Challenge
            </p>
            <p className="mt-1 text-[12.5px]">{lab.challenge.goal}</p>
            {solved ? (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-[12px] font-semibold text-emerald-300"
              >
                ✓ {lab.challenge.success ?? "Target reached — nicely done."}
              </motion.p>
            ) : null}
          </div>
          </div>
        </div>
      ) : (
        <div className="glass rounded-[22px] p-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Observation
          </h2>
          {lab.observation ? (
            <SimErrorBoundary inline resetKey={tab}>
              <ObservationGraph lab={lab} state={stateRef.current} values={values} />
            </SimErrorBoundary>
          ) : (
            <p className="py-6 text-[12px] text-muted-foreground">
              This experiment is read directly off the apparatus — no graph needed.
            </p>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2">
            {readouts.map((r) => (
              <div key={r.label} className="rounded-xl bg-surface/60 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.label}</p>
                <p className="text-[13px] font-semibold tabular-nums">{r.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            {lab.formula.map((f) => (
              <div key={f.expr} className="rounded-xl bg-surface/60 px-3 py-2">
                <p className="font-mono text-[13px] font-semibold">{f.expr}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{f.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Collapsible title="Practice questions">
        <div className="space-y-2">
          {lab.practice.map((item, i) => (
            <div key={item.q} className="rounded-xl bg-surface/60 p-3">
              <p className="text-[12.5px] font-semibold">{item.q}</p>
              <button
                type="button"
                onClick={() => setOpenAnswer(openAnswer === i ? null : i)}
                className="mt-1 text-[11px] font-semibold text-primary"
              >
                {openAnswer === i ? "Hide solution" : "Show solution"}
              </button>
              {openAnswer === i ? (
                <p className="mt-1 text-[12px] text-muted-foreground">{item.a}</p>
              ) : null}
            </div>
          ))}
        </div>
      </Collapsible>

      <Collapsible title="JEE notes" tone="sky">
        <Bullets items={lab.jeeNotes} dot="bg-sky-400" />
      </Collapsible>

      <Collapsible title="Real-life applications" tone="emerald">
        <Bullets items={lab.realLife} dot="bg-emerald-400" />
      </Collapsible>

      <p className="flex items-center justify-center gap-1.5 pb-2 text-[10.5px] text-muted-foreground">
        <Maximize2 className="size-3" /> Drag the apparatus directly on the bench to run the experiment.
      </p>
    </div>
  );
}